import React, { useState } from 'react';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import FormControl from '../../../../Components/Form/FormControl';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useTranslation from '../../../../Hooks/useTranslation';
import WorkplaceSelect from '../../../Workplace/Components/WorkplaceSelect/WokrplaceSelect';
import usePatient from '../../Hooks/usePatient';
import {
	useGetUserOriginalIdQuery,
	useLazyRootWorkplacesPatientQuery,
	WorkplaceMember,
} from '../../../../graphql/graphql';
import { useAppContext } from '../../../../Services/AppContext';
import Loading from '../../../../Components/Loading';
import { useEffectAsync } from '../../../../helpers';
import Text from '../../../../Components/Base/Text/Text';
// @ts-ignore
import styles from './AssignPatientToWorkplace.module.scss';

interface Props {
	patientId: string;
	onSuccess: () => void;
}

const AssignPatientToWorkplace: React.FC<Props> = ({
	patientId,
	onSuccess,
}) => {
	const { assignPatientToWorkplace, resendInvitationToWorkplace } =
		usePatient();
	const { t } = useTranslation();
	const { workplaceId } = useAppContext();
	const { data: userData, isLoading: userDataIsLoading } =
		useGetUserOriginalIdQuery({ id: patientId });
	const [getRootWorkplacesPatient] = useLazyRootWorkplacesPatientQuery();
	const [excludedIds, setExcludedIds] = useState<string[]>([]);
	const [declinedWorkplaces, setDeclinedWorkplaces] = useState<string[]>([]);
	const [workplaceMembers, setWorkplaceMembers] = useState<WorkplaceMember[]>(
		[],
	);

	useEffectAsync(async () => {
		const temp: string[] = [];
		const declined: string[] = [];
		const { data } = await getRootWorkplacesPatient({
			originalId: userData?.user?.originalId,
			workplaceId: workplaceId,
		});
		const workplaces = data?.rootWorkplacesPatient?.data;
		if (workplaces) {
			workplaces.forEach(item => {
				if (item?.workplace?.id) {
					if (item.status === 'DECLINED') {
						declined.push(item.workplace.id);
					} else {
						temp.push(item.workplace.id);
					}
				}
			});
		}
		setWorkplaceMembers(workplaces as WorkplaceMember[]);
		setExcludedIds(temp);
		setDeclinedWorkplaces(declined);
	}, [userData]);

	const validationSchema = yup.object({
		workplace: yup
			.mixed()
			.required(t('validation.assignPatientToWorkplace.workplace')),
	});

	const formMethods = useForm({
		resolver: yupResolver(validationSchema),
	});

	const handleAssignPatient = async (values: any) => {
		if (values?.workplace?.value && patientId) {
			if (declinedWorkplaces.some(item => item === values.workplace.value)) {
				const workplaceMember = workplaceMembers.find(
					item => item?.workplace?.id === values.workplace.value,
				);
				if (workplaceMember) {
					await resendInvitationToWorkplace(
						values.workplace.value,
						workplaceMember.id,
					);
				}
			} else {
				await assignPatientToWorkplace(values.workplace.value, patientId);
			}
		}
		onSuccess();
	};

	if (userDataIsLoading) {
		return <Loading />;
	}

	return (
		<FormWrapper formMethods={formMethods}>
			<Text
				className={styles.text}
				tx='component.assignPatientToWorkplace.infoMessage'
			/>
			<FormControl
				label='component.assignPatientToWorkplace.pickWorkplace'
				name='workplace'
				isRequired>
				<WorkplaceSelect
					excludedIds={excludedIds}
					portal
					filterByRootWorkplaceId
				/>
			</FormControl>
			<div className='mt-3'>
				<SubmitButton
					onSubmit={handleAssignPatient}
					title='component.assignPatientToWorkplace.assign'
				/>
			</div>
		</FormWrapper>
	);
};

export default AssignPatientToWorkplace;
